import React from 'react';
import {
  ComposedChart, Area, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ReferenceLine, ReferenceDot, ResponsiveContainer,
} from 'recharts';

import buildChartRows from './comparableChartTransform';
import { resolveToken } from './themeTokens';

const SUBJECT_COLOR = '#1982C4';
export const COMP_PALETTE = ['#7A5AF8', '#E04F16', '#099250', '#EAAA08', '#DD2590'];

const niceStep = (roughStep) => {
  if (!(roughStep > 0)) return 1;
  const power = Math.floor(Math.log10(roughStep));
  const base = 10 ** power;
  const fraction = roughStep / base;
  let niceFraction = 10;
  if (fraction <= 1) niceFraction = 1;
  else if (fraction <= 2) niceFraction = 2;
  else if (fraction <= 5) niceFraction = 5;
  return niceFraction * base;
};

const buildYTicks = (domainTop) => {
  const step = niceStep(domainTop / 5);
  const ticks = [];
  for (let v = 0; v <= domainTop; v += step) ticks.push(Math.round(v));
  return ticks;
};

const ComparableEventsTooltip = ({ active, payload, label, seriesMeta }) => {
  if (!active || !payload || !payload.length) return null;
  const seen = new Set();
  const rows = payload.filter((p) => {
    if (p.value == null || !seriesMeta[p.dataKey] || seen.has(p.dataKey)) return false;
    seen.add(p.dataKey);
    return true;
  });
  return (
    <div className="mi-ce-tooltip">
      <div className="mi-ce-tooltip-day">D-{label}</div>
      {rows.map((p) => (
        <div
          key={p.dataKey}
          className="mi-ce-tooltip-row"
          style={{ color: p.stroke && p.stroke !== 'none' ? p.stroke : p.fill }}
        >
          {seriesMeta[p.dataKey]}: {Math.round(p.value).toLocaleString()}
        </div>
      ))}
    </div>
  );
};

const ComparableEventsChart = ({
  subject, comps, chart, projection, currentDte, capacity, product, timing, mode,
}) => {
  const tokens = React.useMemo(() => ({
    border: resolveToken('--sl-border-secondary', '#E9EAEB'),
    axisText: resolveToken('--sl-text-quaternary', '#717680'),
    todayLine: resolveToken('--sl-fg-quinary', '#A4A7AE'),
    todayPillBg: resolveToken('--sl-fg-secondary', '#414651'),
    todayPillFg: resolveToken('--sl-text-white', '#FFFFFF'),
    capacityLine: resolveToken('--sl-text-success', '#8AC926'),
    bgPrimary: resolveToken('--sl-bg-primary', '#FFFFFF'),
  }), []);

  const rows = React.useMemo(
    () => buildChartRows(chart || [], { product, timing, mode }),
    [chart, product, timing, mode],
  );

  const showProjection = mode === 'cumulative' && product === 'tickets' && timing === 'all';

  const mergedRows = React.useMemo(() => {
    if (!subject || rows.length === 0) return [];
    const projectionByDte = new Map((projection || []).map((p) => [p.dte, p.value]));
    return rows.map((row) => {
      const subjectValue = row[subject.series_key];
      const merged = { ...row };
      merged.subjectSolid = currentDte != null && row.dte >= currentDte ? subjectValue : null;
      if (showProjection && currentDte != null && row.dte <= currentDte) {
        merged.projection = row.dte === currentDte
          ? subjectValue
          : (projectionByDte.has(row.dte) ? projectionByDte.get(row.dte) : null);
      } else {
        merged.projection = null;
      }
      return merged;
    });
  }, [rows, subject, currentDte, projection, showProjection]);

  if (!subject || mergedRows.length === 0) {
    return (
      <div className="mi-ce-chart-empty">
        No sales-timeline data available yet. Events need recorded sales.
      </div>
    );
  }

  const subjectLabel = `${subject.name} (this event)`;
  const seriesMeta = { [subject.series_key]: subjectLabel, subjectSolid: subjectLabel, projection: 'Projected' };
  comps.forEach((c) => { seriesMeta[c.series_key] = c.name; });

  const maxDte = mergedRows[0].dte;

  let maxValue = 0;
  mergedRows.forEach((row) => {
    if (row[subject.series_key] != null) maxValue = Math.max(maxValue, row[subject.series_key]);
    comps.forEach((c) => { if (row[c.series_key] != null) maxValue = Math.max(maxValue, row[c.series_key]); });
    if (row.projection != null) maxValue = Math.max(maxValue, row.projection);
  });

  const capacityValue = capacity || 0;
  const domainTop = showProjection ? Math.max(capacityValue, maxValue) : maxValue;
  const yDomainMax = domainTop > 0 ? domainTop * 1.04 : 10;
  const yTicks = buildYTicks(domainTop > 0 ? domainTop : 10);

  const xTicks = [];
  for (let d = 0; d <= maxDte; d += 10) xTicks.push(d);

  const renderXTick = ({ x, y, payload }) => (
    <g transform={`translate(${x},${y})`}>
      <text
        x={0}
        y={0}
        dy={14}
        textAnchor="middle"
        fontSize={12}
        fill={tokens.axisText}
        style={{ fontVariantNumeric: 'tabular-nums' }}
      >
        {payload.value === 0 ? 'Event day' : `D-${payload.value}`}
      </text>
    </g>
  );

  const hasToday = currentDte != null && currentDte >= 0 && currentDte <= maxDte;
  const todayRow = hasToday ? mergedRows.find((r) => r.dte === currentDte) : null;
  const todayY = todayRow ? todayRow[subject.series_key] : null;

  const renderTodayLabel = ({ viewBox }) => {
    const width = 88;
    const height = 22;
    const cx = viewBox.x;
    const top = viewBox.y - height - 6;
    return (
      <g>
        <rect x={cx - width / 2} y={top} width={width} height={height} rx={6} fill={tokens.todayPillBg} />
        <text
          x={cx}
          y={top + height / 2 + 4}
          textAnchor="middle"
          fontSize={12}
          fontWeight={600}
          fill={tokens.todayPillFg}
        >
          {`Today · D-${currentDte}`}
        </text>
      </g>
    );
  };

  return (
    <div className="mi-ce-chart">
      <ResponsiveContainer width="100%" height={340}>
        <ComposedChart data={mergedRows} margin={{ top: 32, right: 16, bottom: 8, left: 0 }}>
          <CartesianGrid stroke={tokens.border} vertical={false} />
          <XAxis
            dataKey="dte"
            type="number"
            domain={[0, maxDte]}
            reversed
            ticks={xTicks}
            allowDecimals={false}
            tick={renderXTick}
            tickLine={false}
            axisLine={{ stroke: tokens.border }}
          />
          <YAxis
            domain={[0, yDomainMax]}
            ticks={yTicks}
            tick={{ fontSize: 12, fill: tokens.axisText, fontVariantNumeric: 'tabular-nums' }}
            tickLine={false}
            axisLine={false}
            tickFormatter={(v) => v.toLocaleString()}
            width={56}
          />
          <Tooltip content={<ComparableEventsTooltip seriesMeta={seriesMeta} />} />
          {showProjection && capacityValue > 0 && (
            <ReferenceLine
              y={capacityValue}
              stroke={tokens.capacityLine}
              strokeDasharray="2 4"
              label={{
                value: `Capacity ${capacityValue.toLocaleString()}`,
                position: 'insideTopRight',
                fill: tokens.capacityLine,
                fontSize: 12,
                fontWeight: 600,
              }}
            />
          )}
          {hasToday && (
            <ReferenceLine
              x={currentDte}
              stroke={tokens.todayLine}
              strokeDasharray="4 4"
              label={renderTodayLabel}
            />
          )}
          {comps.map((c, i) => (
            <Line
              key={c.series_key}
              type="monotone"
              dataKey={c.series_key}
              stroke={COMP_PALETTE[i % COMP_PALETTE.length]}
              strokeWidth={1.75}
              strokeOpacity={0.85}
              dot={false}
              isAnimationActive={false}
              connectNulls
              name={c.name}
            />
          ))}
          <Area
            type="monotone"
            dataKey="subjectSolid"
            stroke="none"
            fill={SUBJECT_COLOR}
            fillOpacity={0.07}
            isAnimationActive={false}
            connectNulls
            legendType="none"
          />
          {showProjection && (
            <Line
              type="monotone"
              dataKey="projection"
              stroke={SUBJECT_COLOR}
              strokeWidth={2}
              strokeDasharray="5 5"
              strokeOpacity={0.5}
              dot={false}
              isAnimationActive={false}
              connectNulls
              name="Projected"
            />
          )}
          <Line
            type="monotone"
            dataKey="subjectSolid"
            stroke={SUBJECT_COLOR}
            strokeWidth={3}
            dot={false}
            isAnimationActive={false}
            connectNulls
            name={subject.name}
          />
          {hasToday && todayY != null && (
            <ReferenceDot
              x={currentDte}
              y={todayY}
              r={5}
              fill={SUBJECT_COLOR}
              stroke={tokens.bgPrimary}
              strokeWidth={2}
              isFront
            />
          )}
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
};

export default ComparableEventsChart;
